import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Fade } from "react-awesome-reveal";
import { LinkContext } from "../../LinkProvider";

import SectionTitle from "../home/SectionTitle";

import controlPhoto from "../../img/whyus/control-photo.webp";

const BTN_GRADIENT_WHITE =
  "linear-gradient(-84deg, rgba(255,255,255,0.4) 6%, rgba(204,222,255,0.4) 66%)";

function FeatureItem({ titleKey, textKey, t, white = false }) {
  return (
    <Fade triggerOnce direction="up" duration={600}>
      <div className="flex flex-col gap-[15px] max-w-[309px]">
        <div className="flex items-center gap-[15px]">
          <div
            className={`w-[18px] h-[18px] rounded-[2px] flex items-center justify-center shrink-0 ${
              white ? "bg-white" : "bg-[#bfccd7]"
            }`}
          >
            <Check className="w-[12px] h-[12px] text-[#1c385f]" strokeWidth={2.5} />
          </div>
          <span
            className={`font-['Inter'] font-bold text-[14px] lg:text-[16px] ${
              white ? "text-white" : "text-[#1c385f]"
            }`}
          >
            {t(titleKey)}
          </span>
        </div>
        <p
          className={`font-['Inter'] font-normal text-[14px] leading-[1.5] ${
            white ? "text-white" : "text-[#435c7f]"
          }`}
        >
          {t(textKey)}
        </p>
      </div>
    </Fade>
  );
}

/* Тёмная карточка с текстом и кнопкой (слева в мозаике) */
function EdgeCard({ t, link, className = "" }) {
  return (
    <Fade triggerOnce direction="up" duration={700}>
      <div className={`bg-[#1c385f] rounded-[25px] p-[25px] lg:p-[30px] lg:pt-[60px] ${className}`}>
        <p className="font-['Inter'] font-black text-[24px] lg:text-[32px] leading-[1.4] text-white">
          {t("whyUs.control.card.title1")}
          <br />
          {t("whyUs.control.card.title2")}
        </p>
        <p className="mt-[15px] font-['Inter'] font-normal text-[14px] lg:text-[16px] leading-[1.5] text-white max-w-[323px]">
          {t("whyUs.control.card.text")}
        </p>
        <a
          href={link}
          className="mt-[25px] lg:mt-[35px] inline-flex items-center justify-center px-[32px] lg:px-[42px] py-[14px] lg:py-[18px] rounded-[40px] border border-white font-['Inter'] font-bold text-[14px] lg:text-[16px] text-white capitalize backdrop-blur-[15px] hover:opacity-90 transition-opacity"
          style={{ backgroundImage: BTN_GRADIENT_WHITE }}
        >
          {t("whyUs.control.card.cta")}
        </a>
      </div>
    </Fade>
  );
}

function Control() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);

  return (
    <section className="mt">
      <div className="glcontainer">
        <div className="hidden lg:block">
          <SectionTitle
            title1={t("whyUs.control.title1")}
            title2={t("whyUs.control.title2")}
            text={t("whyUs.control.subtitle")}
            className="mx-auto"
          />
        </div>
        <div className="lg:hidden">
          <SectionTitle
            title1={t("whyUs.control.title1")}
            title2={t("whyUs.control.title2")}
            text={t("whyUs.control.subtitle")}
            align="left"
          />
        </div>

        {/* Desktop: мозаика */}
        <div className="hidden lg:block relative mt-[60px] h-[413px]">
          {/* Левая тёмная карточка с текстом и кнопкой */}
          <EdgeCard t={t} link={link} className="absolute left-0 top-0 w-[34.3%] h-full" />

          {/* Середина: светлый + голубой блоки */}
          <div className="absolute left-[34.3%] top-[31px] w-[32.9%] h-[175px] bg-[#f8faff] flex items-center px-[27px]">
            <FeatureItem titleKey="whyUs.control.feature1.title" textKey="whyUs.control.feature1.text" t={t} />
          </div>
          <div className="absolute left-[34.3%] top-[206px] w-[32.9%] h-[176px] bg-[#e4edf7] flex items-center px-[27px]">
            <FeatureItem titleKey="whyUs.control.feature2.title" textKey="whyUs.control.feature2.text" t={t} />
          </div>

          {/* Правая карточка с фото и тёмным оверлеем */}
          <div className="absolute left-[67.2%] top-[31px] right-0 h-[351px] rounded-r-[25px] overflow-hidden">
            <img
              src={controlPhoto}
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover object-[50%_27%]"
            />
            <div className="absolute inset-0 bg-[rgba(10,22,40,0.8)]" />
            <div className="absolute left-[30px] bottom-[30px] right-[24px]">
              <FeatureItem titleKey="whyUs.control.feature3.title" textKey="whyUs.control.feature3.text" t={t} white />
            </div>
          </div>
        </div>

        {/* Mobile / tablet: тёмная карточка + список преимуществ */}
        <div className="lg:hidden mt-[25px] flex flex-col gap-[25px]">
          <EdgeCard t={t} link={link} />
          <div className="flex flex-col gap-[25px] sm:grid sm:grid-cols-2">
            <FeatureItem titleKey="whyUs.control.feature1.title" textKey="whyUs.control.feature1.text" t={t} />
            <FeatureItem titleKey="whyUs.control.feature2.title" textKey="whyUs.control.feature2.text" t={t} />
            <FeatureItem titleKey="whyUs.control.feature3.title" textKey="whyUs.control.feature3.text" t={t} />
          </div>
        </div>
      </div>
    </section>
  );
}

export default Control;
